import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { BurnedInAnnotationStatus } from '~/domain/dicom/burnedInAnnotation';
import type { Image3D } from '~/domain/patient/image3d';
import {
    useActiveTileRenderer,
    useIsGridPaneActivated,
} from '~/hooks/viewer/activeTileRenderer';
import {
    viewportBurnedInStatus,
    viewportImagesAwaitingReview,
} from '~/hooks/viewer/screenshot';
import {
    reviewedBurnedInImagesState,
    showSensitiveInfoState,
} from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import { enqueueSnackbar } from '~/utils/notifications';
import type { RenderAdapter } from '~/viewportGrid';

/** The exact frame a pending burned-in confirmation was opened against. */
interface PendingUnknownReview {
    /** The tile renderer the confirmation was opened on. */
    renderer: RenderAdapter;
    frameId: number;
}

/**
 * Why no capture can start, when the cause is one the user can act on: no
 * grid pane is activated, or the activated pane shows no image to capture.
 */
export type ScreenshotUnavailableReason = 'noActiveTile' | 'paneShowsNoImage';

export interface ScreenshotCapture {
    /** True when a capture can be started (a tile is activated and none is running). */
    canCapture: boolean;
    /**
     * Why the capture is unavailable, when the user can do something about
     * it. Undefined while a capture is possible or merely in flight.
     */
    unavailableReason: ScreenshotUnavailableReason | undefined;
    /** True while a capture is in flight. */
    isCapturing: boolean;
    /** True while the burned-in confirmation dialog is open. */
    isConfirmOpen: boolean;
    /** Start a capture, running the burned-in PHI gate first. */
    requestCapture: () => void;
    /** Confirm the pending Unknown-status capture and proceed. */
    confirmCapture: () => void;
    /** Dismiss the confirmation without capturing. */
    cancelCapture: () => void;
}

/** Why no tile can be captured, or undefined while one is activated. */
function unavailableReasonOf(
    target: RenderAdapter | undefined,
    isPaneActivated: boolean
): ScreenshotUnavailableReason | undefined {
    if (target) {
        return undefined;
    }
    return isPaneActivated ? 'paneShowsNoImage' : 'noActiveTile';
}

/** No image is treated as pre-vetted — every Unknown source prompts. */
const NO_SUPPRESSION: ReadonlySet<Image3D> = new Set();

/**
 * Owns the viewport-screenshot capture policy — the burned-in-PHI safety gate —
 * outside the UI layer, per the {@link useSensitiveInfoReveal} precedent and the
 * DDD layering in CLAUDE.md. {@link ScreenshotTool} renders only a button and a
 * confirmation dialog wired to this hook, so the gate can be unit-tested against
 * the real {@link viewportBurnedInStatus} rather than a mock of it.
 *
 * The capture target is the grid tile the user activated, a maximized tile
 * included, read through its renderer: the gate reads the frame the tile has
 * published, and the tile names the file after its own plane (or oblique
 * when its view is turned off the patient axes). The capture is unavailable
 * while no tile renderer is published. The reason is `paneShowsNoImage` when
 * the grid reports an activated pane without one, and `noActiveTile`
 * otherwise. A published renderer that has no frame at the click draws no
 * image either, so the click is refused with the same words.
 *
 * The pixel gate has three verdicts (see {@link viewportBurnedInStatus}):
 * - **Yes** — DICOM declares burned-in identifiers: hard-blocked, never captured.
 * - **No** — every source affirms it is clean: captured directly.
 * - **Unknown** — cannot be affirmed (the normal case; the attribute is Type 3 and
 *   usually absent): captured only after the user confirms they have visually
 *   checked the pixels for identifiers.
 *
 * How often the Unknown confirmation is asked is tied to the patient-information
 * visibility toggle ({@link showSensitiveInfoState}):
 * - **Info shown** (not obscured) — prompt on *every* capture, so a screenshot taken
 *   while identifiers could be on screen always forces a fresh check.
 * - **Info hidden** — prompt *once per image* for the session (honouring
 *   {@link reviewedBurnedInImagesState}), the alarm-fatigue-avoiding path.
 *
 * Hiding info therefore *relaxes* prompting but never *suppresses* it: the first
 * capture of an Unknown image still prompts, and a declared Yes is still blocked,
 * because the hide toggle only removes the DOM banner — it does not de-identify
 * the image pixels the screenshot serialises.
 */
export default function useScreenshotCapture(): ScreenshotCapture {
    const intl = useIntl();
    const target = useActiveTileRenderer();
    const isPaneActivated = useIsGridPaneActivated();
    const selectedRoiId = useActiveEditTarget().roiId ?? undefined;
    const [reviewedImages, setReviewedImages] = useRecoilState(
        reviewedBurnedInImagesState
    );
    const isPatientInfoShown = useRecoilValue(showSensitiveInfoState);
    const [isCapturing, setIsCapturing] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [pendingUnknownReview, setPendingUnknownReview] =
        useState<PendingUnknownReview | null>(null);

    const runCapture = async (renderer: RenderAdapter): Promise<void> => {
        setIsCapturing(true);
        try {
            await renderer.captureScreenshot({
                roiId: selectedRoiId,
                capturedAt: new Date(),
            });
        } catch (error) {
            handleError(error, 'Failed to capture viewport screenshot');
        } finally {
            setIsCapturing(false);
        }
    };

    const requestCapture = (): void => {
        if (!target) {
            return;
        }
        const frame = target.getPublishedFrame();
        if (!frame) {
            // A renderer without a mounted view draws nothing: the pane shows
            // no image. The click must not vanish, so it is refused with that
            // reason rather than reported as a failure.
            enqueueSnackbar(
                intl.formatMessage({ id: 'tool.disabled.paneShowsNoImage' }),
                { variant: 'warning' }
            );
            return;
        }
        // Info shown → honour no prior attestation, so every capture re-prompts.
        // Info hidden → honour the session attestations, so an image prompts once.
        // Yes is never downgraded by either set.
        const suppression = isPatientInfoShown
            ? NO_SUPPRESSION
            : reviewedImages;
        switch (viewportBurnedInStatus(frame, suppression)) {
            case BurnedInAnnotationStatus.Yes:
                // Hard block: the image declares burned-in identifiers, so it must
                // never be captured for sharing.
                enqueueSnackbar(
                    intl.formatMessage({ id: 'screenshot.burnedIn.blocked' }),
                    { variant: 'warning' }
                );
                return;
            case BurnedInAnnotationStatus.Unknown:
                // Cannot be affirmed safe and not covered by the current prompting
                // policy — require explicit confirmation before sharing pixels that
                // may carry burned-in PHI. Bind the prompt to this exact frame.
                setPendingUnknownReview({
                    renderer: target,
                    frameId: frame.canvasFrameId,
                });
                setIsConfirmOpen(true);
                return;
            default:
                void runCapture(target);
        }
    };

    const confirmCapture = (): void => {
        setIsConfirmOpen(false);
        const pending = pendingUnknownReview;
        setPendingUnknownReview(null);
        const frame = target?.getPublishedFrame();
        if (
            !pending ||
            !target ||
            !frame ||
            target !== pending.renderer ||
            frame.canvasFrameId !== pending.frameId
        ) {
            // The displayed frame changed (or vanished) while the dialog was open,
            // so the confirmation no longer describes what is on screen — re-run the
            // gate from scratch rather than attest to a frame the user did not see.
            requestCapture();
            return;
        }
        // Record the attestation against the images actually on the confirmed
        // frame — regardless of the visibility toggle, so a later hidden-mode
        // capture of the same image is not re-prompted. Read before the capture
        // runs: it describes the frame the user was looking at when they confirmed.
        const justReviewed = viewportImagesAwaitingReview(
            frame,
            reviewedImages
        );
        if (justReviewed.length > 0) {
            setReviewedImages(new Set([...reviewedImages, ...justReviewed]));
        }
        void runCapture(target);
    };

    const cancelCapture = (): void => {
        setIsConfirmOpen(false);
        setPendingUnknownReview(null);
    };

    return {
        canCapture: !!target && !isCapturing,
        unavailableReason: unavailableReasonOf(target, isPaneActivated),
        isCapturing,
        isConfirmOpen,
        requestCapture,
        confirmCapture,
        cancelCapture,
    };
}
